<template>
  <!-- Expanded card — dark primary background, title, description, CTA, and a
       close button that collapses (not permanently dismisses) to the chip. -->
  <div v-if="!isCollapsed" class="new-dashboard-banner">
    <button
      class="new-dashboard-banner__close"
      type="button"
      :aria-label="$t('new_dashboard_banner.close')"
      @click="collapse"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>

    <h3 class="new-dashboard-banner__title">{{ $t('new_dashboard_banner.title') }}</h3>
    <p class="new-dashboard-banner__description">{{ $t('new_dashboard_banner.description') }}</p>
    <button class="new-dashboard-banner__cta" type="button" :disabled="isLoading" @click="onCtaClick">
      <span v-if="isLoading" class="new-dashboard-banner__spinner" />
      {{ $t('new_dashboard_banner.cta') }}
    </button>
  </div>

  <!-- Collapsed chip — small pill with a grid icon and label. Click to
       expand back into the full banner. The state is persisted so the
       chip stays put across reloads once the partner has interacted. -->
  <button
    v-else
    class="new-dashboard-chip"
    type="button"
    :aria-label="$t('new_dashboard_banner.title')"
    @click="expand"
  >
    <svg
      class="new-dashboard-chip__icon"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <line x1="17.5" y1="14" x2="17.5" y2="21" />
      <line x1="14" y1="17.5" x2="21" y2="17.5" />
    </svg>
    <span>{{ $t('new_dashboard_banner.chipLabel') }}</span>
  </button>
</template>

<script lang="ts">
import { defineComponent, ref } from '@vue/composition-api';
import { switchToNewDashboard } from '@/helpers/switchToNewDashboard';

export default defineComponent({
  name: 'NewDashboardBanner',
  setup() {
    // Start expanded so partners see the full pitch on load. Clicking the
    // close (×) collapses to a chip for the current session; a reload
    // brings the full card back.
    const isCollapsed = ref(false);
    const isLoading = ref(false);

    const onCtaClick = async () => {
      if (isLoading.value) return;
      isLoading.value = true;
      const ok = await switchToNewDashboard();
      if (!ok) {
        // Silent-ish on failure: unfreeze the button so the partner can
        // retry, without popping a toast every time they poke the CTA.
        isLoading.value = false;
      }
      // On success we're already navigating away; keep the button
      // disabled so a fast double-click can't fire a second OTL.
    };

    const collapse = () => {
      isCollapsed.value = true;
    };

    const expand = () => {
      isCollapsed.value = false;
    };

    return {
      isCollapsed,
      isLoading,
      onCtaClick,
      collapse,
      expand,
    };
  },
});
</script>

<style lang="scss">
/* Design tokens: matches tailwind.config.js `primary.*` so the banner and
   chip stay in sync with any future palette shift. */
$new-pd-bg: theme('colors.primary.700');           // #5D4EC3 — matches sidebar (bg-primary-700)
$new-pd-bg-hover: theme('colors.primary.800');     // #463B92 — one step darker for chip hover
$new-pd-accent: theme('colors.primary.DEFAULT');   // #9081F6
$new-pd-accent-hover: theme('colors.primary.600'); // #7462F4
$new-pd-shadow: 0 20px 60px rgba(93, 78, 195, 0.35);

/* ---------- expanded card ---------- */
.new-dashboard-banner {
  position: fixed !important;
  bottom: 24px;
  right: 24px;
  z-index: 99999;
  max-width: 480px;
  width: calc(100% - 48px);
  background-color: $new-pd-bg;
  border-radius: 16px;
  padding: 24px;
  box-shadow: $new-pd-shadow;
  color: theme('colors.white.pureWhite');
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  animation: newDashboardBannerSlideUp 0.4s ease-out;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

@keyframes newDashboardBannerSlideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.new-dashboard-banner__close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 4px;
  line-height: 0;
  transition: color 0.2s;

  &:hover {
    color: #fff;
  }
}

[dir='rtl'] .new-dashboard-banner__close {
  right: auto;
  left: 12px;
}

[dir='rtl'] .new-dashboard-banner {
  right: auto;
  left: 24px;
}

.new-dashboard-banner__title {
  font-size: 18px;
  font-weight: 700;
  margin: 0;
  line-height: 1.4;
  color: #fff;
}

.new-dashboard-banner__description {
  font-size: 14px;
  font-weight: 400;
  margin: 0;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
}

.new-dashboard-banner__cta {
  margin-top: 8px;
  align-self: flex-start;
  background-color: $new-pd-accent;
  color: #fff;
  border: none;
  border-radius: 24px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, opacity 0.2s;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background-color: $new-pd-accent-hover;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.new-dashboard-banner__spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: newDashboardBannerSpin 0.6s linear infinite;
}

@keyframes newDashboardBannerSpin {
  to {
    transform: rotate(360deg);
  }
}

/* ---------- collapsed chip ----------
   Sits a bit higher off the viewport bottom (40px instead of the banner's
   24px) so it clears floating widgets like the Intercom launcher and any
   page-level footer/CTA that hugs the bottom edge. */
.new-dashboard-chip {
  position: fixed !important;
  bottom: 40px;
  right: 24px;
  z-index: 99999;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background-color: $new-pd-bg;
  color: #fff;
  border: none;
  border-radius: 999px;
  box-shadow: $new-pd-shadow;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, background-color 0.2s;
  animation: newDashboardBannerSlideUp 0.3s ease-out;

  &:hover {
    background-color: $new-pd-bg-hover;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

[dir='rtl'] .new-dashboard-chip {
  right: auto;
  left: 24px;
}

.new-dashboard-chip__icon {
  color: $new-pd-accent;
  flex-shrink: 0;
}

@media (max-width: 540px) {
  .new-dashboard-banner {
    bottom: 16px;
    right: 16px;
    width: calc(100% - 32px);
    padding: 16px;
  }

  .new-dashboard-chip {
    bottom: 24px;
    right: 16px;
  }

  [dir='rtl'] .new-dashboard-chip,
  [dir='rtl'] .new-dashboard-banner {
    right: auto;
    left: 16px;
  }
}
</style>
